import { ApiHosts, handleResponseWithNon200Errors, makeGet, makePost } from '@/redux/api/helpers';
import { SavedCreditCard } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.types';
import { CreditCard, CreditCardAddress as Address } from '@/types/CreditCard';

/**
 * @category Payments API
 * @see getLAPaymentCards
 */
export type GetLAPaymentCardsParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
};

export type GetLAPaymentCardsPayload = {
    allowedToAddCards: boolean;
    cards: CreditCard[];
};

export type GetLAPaymentCardsResponse = {
    error: boolean;
    payload: GetLAPaymentCardsPayload;
};

/**
 * getLAPaymentCards - gets a bidder's credit card on file
 * @category Payments API
 * @function getLAPaymentCards
 * @param {GetLAPaymentCardsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/cards/
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/72dull/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/get_cards__bidderId_
 */
export const getLAPaymentCards = ({ authToken, bidderId, deployment }: GetLAPaymentCardsParams) =>
    new Promise<GetLAPaymentCardsResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: `cards/${bidderId}`,
            authToken,
            deployment,
            forceCredentials: true,
            path: ApiHosts.Redstripe,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

/**
 * Used as the response type of the following:
 * @see {PostAddLACardParams}
 * @see {PostUpdateLACardParams}
 * @see {PostAddZipCardParams}
 */
export type PostCreditCardResponse = {
    error: boolean;
    payload: SavedCreditCard;
};

/**
 * @category Payments API
 * @see postAddLACard
 */
type PostAddLACardParams = {
    authToken: string;
    bidderId: number;
    billingAddress: Address;
    cardNonce: string;
    cardholderName: string;
    deployment: string;
    houseId: number;
    providerId: number;
};

/**
 * postAddLACard - adds a credit card to for Payrix and Paysafe's card-on-file for bidder
 * @category Payments API
 * @function postAddLACard
 * @param {PostAddLACardParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/cards/
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/72dull/methods/POST
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/post_cards__bidderId_
 */
export const postAddLACard = ({
    authToken,
    bidderId,
    billingAddress,
    cardholderName,
    cardNonce,
    deployment,
    houseId,
    providerId,
}: PostAddLACardParams) =>
    new Promise<PostCreditCardResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: `cards/${bidderId}/add`,
            authToken,
            deployment,
            forceCredentials: true,
            path: ApiHosts.Redstripe,
        });
        request.send({
            billingAddress,
            cardholderName,
            cardNonce,
            defaultCard: true,
            houseId,
            providerId,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

/**
 * @category Payments API
 * @see postUpdateLACard
 */
type PostUpdateLACardParams = {
    authToken: string;
    bidderId: number;
    billingAddress?: Address;
    cardId: number;
    defaultCard: boolean;
    deployment: string;
    enabled: boolean;
};

/**
 * postUpdateLACard - updates an existing bidder's card-on-file
 * @category Payments API
 * @function postUpdateLACard
 * @param {PostUpdateLACardParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/bidder/${bidderId}/card
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/upmpo6q7pc/resources/72dull/methods/POST
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/payment-redstripe/1.0.0#/default/post_bidder__bidderId__card__cardId_
 */
export const postUpdateLACard = ({
    authToken,
    bidderId,
    billingAddress,
    cardId,
    defaultCard,
    deployment,
    enabled,
}: PostUpdateLACardParams) =>
    new Promise<PostCreditCardResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: `bidder/${bidderId}/card/${cardId}`,
            authToken,
            deployment,
            path: ApiHosts.Payment,
        });
        request.send({
            billingAddress,
            defaultCard,
            enabled,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

/**
 * @category Payments API
 * @see postAddQuadypayCard
 */
type PostAddZipCardParams = {
    authToken: string;
    bidderId: number;
    billingAddress: Address;
    brand: string;
    deployment: string;
    firstName: string;
    houseId: number;
    lastName: string;
    month: number;
    number: string;
    providerId: number;
    securityCode: string;
    year: string;
};

/**
 * postAddZipCard - adds a single use credit card to use for a bidder.
 * Quadpay had a rebrand, so the front end has all been renamed to ZIP
 * @category Payments API
 * @function postAddZipCard
 * @param {PostAddZipCardParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/payment/cards/{bidderId}/quadpay
 */
export const postAddZipCard = ({
    authToken,
    bidderId,
    billingAddress,
    brand,
    deployment,
    firstName,
    houseId,
    lastName,
    month,
    number,
    providerId,
    securityCode,
    year,
}: PostAddZipCardParams) =>
    new Promise<PostCreditCardResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: `cards/${bidderId}/quadpay`,
            authToken,
            deployment,
            path: ApiHosts.Payment,
        });
        request.send({
            billingAddress,
            brand,
            firstName,
            houseId,
            lastName,
            month,
            number,
            providerId,
            securityCode,
            year,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });
